/**
 * @module core/toast
 *
 * Accessible, non-blocking user feedback.
 *
 * Two ARIA live regions are used: a polite one for confirmations and an
 * assertive one for errors. This is what "user feedback for interactive
 * elements" means in practice — the user is always told what happened.
 *
 * The module intentionally avoids a hard dependency on GSAP so that it keeps
 * working even if the animation layer fails to initialise (feedback matters
 * most precisely when something is broken).
 */

import { el, qs } from "./dom.js";
import { escapeHtml, icon } from "../svg/icons.js";

/** @typedef {"info"|"success"|"warning"|"error"} ToastType */

const ICONS = {
  info: "spark",
  success: "check",
  warning: "spark",
  error: "spark",
};

const DEFAULT_DURATION = 5200;

/** @type {HTMLElement|null} */
let politeRegion = null;
/** @type {HTMLElement|null} */
let assertiveRegion = null;

/**
 * Lazily create the live-region containers. They are created once and reused,
 * which is important: live regions must exist in the DOM *before* content is
 * inserted for screen readers to announce it reliably.
 *
 * The cached references are re-validated with `isConnected`: if the regions were
 * removed from the document (a framework re-render, a test harness resetting
 * `body.innerHTML`, a hot reload), keeping the stale nodes would silently
 * swallow every notification.
 *
 * @returns {{ polite: HTMLElement, assertive: HTMLElement }}
 */
function ensureRegions() {
  if (politeRegion && !politeRegion.isConnected) politeRegion = null;
  if (assertiveRegion && !assertiveRegion.isConnected) assertiveRegion = null;

  if (!politeRegion) {
    politeRegion = qs("#toast-polite");
    if (!politeRegion) {
      politeRegion = el(
        '<div id="toast-polite" class="toast-region" role="status" aria-live="polite" aria-atomic="true"></div>'
      );
      document.body.appendChild(politeRegion);
    }
  }

  if (!assertiveRegion) {
    assertiveRegion = qs("#toast-assertive");
    if (!assertiveRegion) {
      assertiveRegion = el(
        '<div id="toast-assertive" class="toast-region" role="alert" aria-live="assertive" aria-atomic="true"></div>'
      );
      document.body.appendChild(assertiveRegion);
    }
  }

  return { polite: politeRegion, assertive: assertiveRegion };
}

/**
 * Show a toast message.
 *
 * @param {string} message               Human-readable message.
 * @param {Object} [options]
 * @param {ToastType} [options.type="info"]
 * @param {number} [options.duration]    Milliseconds before auto-dismiss.
 *   Pass `0` to require manual dismissal (used for errors by default).
 * @param {string} [options.actionLabel] Optional action button label.
 * @param {() => void} [options.onAction] Action handler.
 * @returns {() => void} A function that dismisses the toast immediately.
 */
export function toast(message, options = {}) {
  const { type = "info", actionLabel, onAction } = options;
  const duration = options.duration ?? (type === "error" ? 0 : DEFAULT_DURATION);

  if (typeof document === "undefined" || !document.body) {
    // SSR / very early call: fall back to the console so nothing is lost.
    console.warn(`[toast:${type}] ${message}`);
    return () => {};
  }

  const { polite, assertive } = ensureRegions();
  const region = type === "error" ? assertive : polite;

  const node = el(`
    <div class="toast toast--${type}" data-toast-type="${type}">
      <span class="toast__icon" aria-hidden="true">${icon(ICONS[type] || "spark", { size: 16, strokeWidth: 2 })}</span>
      <p class="toast__message">${escapeHtml(message)}</p>
      ${
        actionLabel
          ? `<button type="button" class="toast__action">${escapeHtml(actionLabel)}</button>`
          : ""
      }
      <button type="button" class="toast__close" aria-label="Dismiss notification">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
  `);

  let dismissed = false;
  /** @type {ReturnType<typeof setTimeout>|undefined} */
  let timer;

  const dismiss = () => {
    if (dismissed) return;
    dismissed = true;
    if (timer) clearTimeout(timer);
    node.classList.add("toast--leaving");
    const remove = () => node.remove();
    node.addEventListener("transitionend", remove, { once: true });
    // Safety net in case the transition never fires (reduced motion, display:none).
    setTimeout(remove, 400);
  };

  node.querySelector(".toast__close")?.addEventListener("click", dismiss);

  if (actionLabel && typeof onAction === "function") {
    node.querySelector(".toast__action")?.addEventListener("click", () => {
      onAction();
      dismiss();
    });
  }

  region.appendChild(node);
  // Force a frame so the enter transition runs.
  requestAnimationFrame(() => node.classList.add("toast--visible"));

  if (duration > 0) {
    timer = setTimeout(dismiss, duration);
  }

  return dismiss;
}

/**
 * Convenience wrappers so call sites read clearly.
 *
 * @param {string} message
 * @param {Parameters<typeof toast>[1]} [options]
 */
export const notify = {
  info: (message, options) => toast(message, { ...options, type: "info" }),
  success: (message, options) => toast(message, { ...options, type: "success" }),
  warning: (message, options) => toast(message, { ...options, type: "warning" }),
  error: (message, options) => toast(message, { ...options, type: "error" }),
};

/**
 * Remove all visible toasts. Used by tests and on teardown.
 * @returns {void}
 */
export function clearToasts() {
  document.querySelectorAll?.(".toast").forEach((node) => node.remove());
}
